import { getJson } from './stremio'

interface WikiPage {
  title?: string
  thumbnail?: { source?: string }
}

interface WikiPortraitResponse {
  query?: {
    normalized?: Array<{ from: string; to: string }>
    redirects?: Array<{ from: string; to: string }>
    pages?: Record<string, WikiPage>
  }
}

const cache = new Map<string, string>()
/** Bounded for the same reason as the search cache: this app does not restart. */
const CACHE_LIMIT = 300

function remember(name: string, portrait: string) {
  while (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next().value
    if (oldest === undefined) break
    cache.delete(oldest)
  }
  cache.set(key(name), portrait)
}

function key(value: string) {
  return value.trim().toLocaleLowerCase()
}

/** Map exact requested names through MediaWiki normalization/redirects. Kept
 * pure so bad or incomplete remote results cannot put a wrong face on a card. */
export function mapWikiPortraits(names: string[], data: WikiPortraitResponse) {
  const aliases = new Map<string, string>()
  ;[...(data.query?.normalized || []), ...(data.query?.redirects || [])]
    .forEach((entry) => aliases.set(key(entry.from), key(entry.to)))
  const pages = new Map<string, string>()
  Object.values(data.query?.pages || {}).forEach((page) => {
    if (page.title && page.thumbnail?.source) pages.set(key(page.title), page.thumbnail.source)
  })
  const follow = (name: string) => {
    let current = key(name)
    for (let count = 0; count < 3 && aliases.has(current); count += 1) current = aliases.get(current)!
    return current
  }
  return Object.fromEntries(names.map((name) => [name, pages.get(follow(name)) || '']))
}

/** Wikipedia's `pilicense=free` gate keeps the TV from embedding non-free
 * publicity stills. Missing/ambiguous results stay as the honest silhouette. */
export async function getPersonPortraits(names: string[]) {
  const unique = Array.from(new Set(names.map((name) => name.trim()).filter(Boolean))).slice(0, 12)
  const missing = unique.filter((name) => !cache.has(key(name)))
  if (missing.length) {
    const query = [
      'action=query', 'format=json', 'origin=*', 'redirects=1',
      'prop=pageimages', 'piprop=thumbnail', 'pithumbsize=360', 'pilicense=free',
      `titles=${encodeURIComponent(missing.join('|'))}`,
    ].join('&')
    const mapped = mapWikiPortraits(missing, await getJson<WikiPortraitResponse>(`https://en.wikipedia.org/w/api.php?${query}`, undefined, 12_000))
    missing.forEach((name) => remember(name, mapped[name] || ''))
  }
  return Object.fromEntries(unique.map((name) => [name, cache.get(key(name)) || '']))
}
